import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "20px",
    paddingRight: "20px",
    boxShadow: "0 6px 20px rgba(45,20,43,0.04)",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginBottom: "12px",
  },
  stepperRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  stepperBtn: {
    cursor: "pointer",
    appearance: "none",
    width: "44px",
    height: "44px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    ...shorthands.border("2px", "solid", BRAND.purple),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: BRAND.purple,
      color: BRAND.white,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.3,
    },
  },
  bigNumber: {
    fontFamily: BRAND.fontAccent,
    fontSize: "3rem",
    fontWeight: 600,
    color: BRAND.ink,
    lineHeight: 1,
    minWidth: "70px",
    textAlign: "center",
  },
  bigNumberSuffix: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    marginTop: "12px",
  },
  timelineCard: {
    backgroundColor: BRAND.bg,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  timelineLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginBottom: "16px",
  },
  timelineRow: {
    display: "flex",
    alignItems: "stretch",
    columnGap: "10px",
    overflowX: "auto",
    paddingBottom: "4px",
  },
  yearChip: {
    flexShrink: 0,
    minWidth: "84px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "center",
    fontFamily: BRAND.fontPrimary,
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  yearChipFixed: {
    backgroundColor: "#fff8e8",
    ...shorthands.borderColor(BRAND.gold),
  },
  yearChipPast: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
  },
  yearText: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    fontWeight: 600,
    color: BRAND.ink,
    lineHeight: 1.1,
  },
  yearTag: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginTop: "4px",
  },
});

type Props = {
  yearsBack: number;
  onChange: (next: number) => void;
  selectedYears: number[];
  currentYear: number;
};

const MIN = 1;
const MAX = 10;

const PeriodPicker = ({ yearsBack, onChange, selectedYears, currentYear }: Props) => {
  const styles = useStyles();
  const dec = () => onChange(Math.max(MIN, yearsBack - 1));
  const inc = () => onChange(Math.min(MAX, yearsBack + 1));

  const tagFor = (year: number): string => {
    if (year === currentYear + 1) return "Plan";
    if (year === currentYear) return "Laufend";
    if (year === currentYear - 1) return "Vorjahr";
    return `${currentYear - year}J zurück`;
  };

  const isFixed = (year: number) => year >= currentYear;

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.label}>Wie viele Jahre zurück?</div>
        <div className={styles.stepperRow}>
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={dec}
            disabled={yearsBack <= MIN}
            aria-label="Weniger Jahre"
          >
            −
          </button>
          <div>
            <div className={styles.bigNumber}>{yearsBack}</div>
          </div>
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={inc}
            disabled={yearsBack >= MAX}
            aria-label="Mehr Jahre"
          >
            +
          </button>
          <span className={styles.bigNumberSuffix}>
            {yearsBack === 1 ? "Jahr" : "Jahre"} historisch
          </span>
        </div>
        <div className={styles.hint}>
          Es werden Daten für insgesamt {2 + yearsBack} Jahre erfasst — laufendes
          Jahr, Folgejahr und {yearsBack} {yearsBack === 1 ? "Jahr" : "Jahre"} zurück.
        </div>
      </div>

      <div className={styles.timelineCard}>
        <div className={styles.timelineLabel}>Erfasste Jahre</div>
        <div className={styles.timelineRow}>
          {selectedYears.map((year) => {
            const fixed = isFixed(year);
            return (
              <div
                key={year}
                className={mergeClasses(styles.yearChip, fixed ? styles.yearChipFixed : styles.yearChipPast)}
              >
                <div className={styles.yearText}>{year}</div>
                <div className={styles.yearTag}>{tagFor(year)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PeriodPicker;
